import type { ReactNode } from 'react';
import { Badge } from '@heyroot/ui/primitives/Badge';
import type { BusinessTypeVersion } from '../../lib/api';

/**
 * Rail (published · draft · history) — Spec §6.2. Versioning only earns its
 * complexity if published-vs-draft is legible at a glance, so each lane gets
 * its own status badge rather than one flat list sorted by number.
 */
export function VersionRail({
  versions,
  selectedId,
  onSelect,
}: {
  versions: BusinessTypeVersion[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const published = versions.find((v) => v.status === 'published') ?? null;
  const draft = versions.find((v) => v.status === 'draft') ?? null;
  const history = versions
    .filter((v) => v.status === 'archived')
    .sort((a, b) => b.versionNumber - a.versionNumber);

  const item = (v: BusinessTypeVersion, badge: ReactNode) => (
    <button
      key={v.id}
      type="button"
      onClick={() => onSelect(v.id)}
      aria-current={v.id === selectedId}
      className={`w-full rounded-control border p-3 text-left transition-colors ${
        v.id === selectedId
          ? 'border-brand-primary bg-surface-raised'
          : 'border-border bg-surface hover:bg-surface-sunken'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-body-sm-emphasis tabular-nums text-primary">v{v.versionNumber}</span>
        {badge}
      </div>
      {v.changeSummary && (
        <p className="mt-1 line-clamp-2 text-eyebrow text-muted">{v.changeSummary}</p>
      )}
    </button>
  );

  return (
    <nav aria-label="Template versions" className="flex flex-col gap-2">
      {published && item(published, <Badge variant="success">Published</Badge>)}
      {draft && item(draft, <Badge variant="info">Draft</Badge>)}
      {history.length > 0 && (
        <>
          <div className="mt-2 text-eyebrow uppercase text-muted">History</div>
          {history.map((v) => item(v, <Badge variant="neutral">Archived</Badge>))}
        </>
      )}
    </nav>
  );
}
